import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Pagination from "../../Reusablecomponents/Pagination";
import ExportButton from "../../Reusablecomponents/ExportButton";
import { FaPlus, FaEye, FaFileExport } from "react-icons/fa";
import "../../css/others/Table.css";
import { apiCall } from "../../main/ApiCall";
import { buildUserPayload } from "../../main/buildUserPayload";
import { campaignStatusEnums, createToastMessage, ToastMessage } from "../../interFaces/interfaces";
import * as XLSX from 'xlsx-js-style';
import ToastPopup from "../../popups/ToastPopup";
import EncryptionUtils from "../../main/encryptionUtils";
const AuditorCheckListManagement: React.FC = () => {
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage, setItemsPerPage] = useState<number>(10);
  const [totalElements, setTotalElements] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(0);
  const navigate = useNavigate();
  const [fields, setFields] = useState<any>({});
  const clientId = localStorage.getItem("clientId");
  const [extensionDays, setExtensionDays] = useState<number>(0);
  const [campaignStatusEnums, setCampaignStatusEnums] = useState<campaignStatusEnums[]>([]);
  const [message, setMessage] = useState<ToastMessage | null>(null);
  const userInfo = EncryptionUtils.get<any>("userInfo", null);
  const userId = userInfo?.id;
  const createdBy = userInfo?.email;
  const columns = [{ key: "serialNumber", label: "S.No." }, { key: "campaignName", label: "Campaign Name" }, { key: "projectId", label: "Project ID" }, { key: "assignedCheckList", label: "Checklist Name" }, { key: "auditStatus", label: "Audit Status" }, { key: "actions", label: "Action" }];
  const exportColumns = columns.filter((col) => col.key !== "serialNumber" && col.key !== "actions");
  useEffect(() => {
    const permissions = EncryptionUtils.get<any>("permissions", null);
    if (permissions.tabs && permissions.tabs["checkListExecution"]) {
      setFields(permissions.tabs["checkListExecution"].fields);
    } else {
      console.log("No permissions found for 'checkListExecution' tab");
    }
  }, []);
  const fetchData = useCallback(async (page: number, size: number) => {
    setLoading(true);
    const payload = buildUserPayload();
    try {
      const requestBody = [{ field: "clientId", operator: "=", value: clientId }, ...(payload ? [payload] : [])];
      const response = await apiCall("post", `/api/campaign-projects/search?page=${page}&size=${size}`, requestBody);
      const { content = [], totalElements = 0, totalPages = 0 } = response || {};
      setData(content);
      setTotalElements(totalElements);
      setTotalPages(totalPages);
    } catch (error) {
      console.error("Error fetching data:", error);
      setData([]);
      setTotalElements(0);
      setTotalPages(0);
    } finally {
      setLoading(false);
    }
  }, [clientId]);
  useEffect(() => {
    const fetchClientConfig = async () => {
      try {
        const response = await apiCall("get", `/api/client-config/${clientId}`);
        setExtensionDays(response?.extensionDays || 0);
      } catch (error) {
        console.error("Error fetching client config:", error);
      }
    };
    fetchClientConfig();
  }, [clientId]);
  const isAuditExpired = (campaignEndDate: string) => {
    if (!campaignEndDate) return false;
    const endDate = new Date(campaignEndDate);
    endDate.setDate(endDate.getDate() + extensionDays);
    const today = new Date();
    return today > endDate;
  };
  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
    fetchData(page - 1, itemsPerPage);
  }, [itemsPerPage, fetchData]);
  const handleItemsPerPageChange = useCallback((size: number) => {
    setItemsPerPage(size);
    setCurrentPage(1);
    fetchData(0, size);
  }, [fetchData]);
  const handleExportChecklist = async (localTemplateId: any, templateName: any, campaignId: any, projectId: any) => {
    try {
      const requestBody = { campaignId, auditorId: userId, projectId, createdBy };
      const response = await apiCall("post", `/api/audit/campaign/auditor/questions`, requestBody, { page: 0, size: 2_147_483_647 });
      if (!response || !response.responses) {
        setMessage(createToastMessage("No data available to export.", "warning", 3000));
        return;
      }
      const columnConfig = { questionText: "Question", practiceArea: "Control/Practice Area", typeOfFindings: "Type of Findings", findings: "Findings", pims: "PIMS", isms: "ISMS", bcms: "BCMS", cmmi: "CMMI", soc1: "SOC1", soc2: "SOC2", qms: "QMS", aims: "AIMS", erm: "ERM", itsm: "ITSM", ers: "ERS", ohs: "OHS", attachments: "Attachments" };
      const columnsToInclude = Object.keys(columnConfig).filter(key => {
        if (key === "attachments") return true;
        if (key === "practiceArea") {
          const hasControlFields = response.responses.some((item: any) => item.pims || item.isms || item.bcms || item.cmmi || item.soc1 || item.soc2 || item.qms || item.aims || item.erm || item.itsm || item.ers || item.ohs);
          return !hasControlFields;
        }
        return response.responses.some((item: any) => item[key]);
      });
      const mappedData = response.responses.map((item: any) => {
        const rowData: any = {};
        columnsToInclude.forEach(column => {
          if (column === "attachments") {
            rowData[columnConfig[column]] = item.attachments ? item.attachments.map((att: any) => att.fileKey).join(", ") : "";
          } else {
            rowData[columnConfig[column as keyof typeof columnConfig]] = item[column as keyof typeof item] || "";
          }
        });
        return rowData;
      });
      const worksheet = XLSX.utils.json_to_sheet(mappedData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Audit Checklist");
      if (mappedData.length > 0) {
        const colWidths = Object.keys(mappedData[0]).map(key => ({
          wch: Math.max(
            key.length,
            ...mappedData.map((row: { [x: string]: any; }) => String(row[key] || "").length)
          )
        }));
        worksheet['!cols'] = colWidths;
      }
      const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
      const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${templateName}_Audit_Checklist_${new Date().toISOString().split('T')[0]}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
      setMessage(createToastMessage("Checklist exported successfully!", "success", 3000));
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || "Export failed.";
      setMessage(createToastMessage(errorMessage, "error", 3000));
    }
  };
  useEffect(() => {
    const fetchStatusEnums = async () => {
      try {
        const response = await apiCall("get", "/api/enums/CampaignStatusEnum");
        setCampaignStatusEnums(response);
      } catch (error) {
        console.error("Error fetching status enums:", error);
      }
    };
    fetchStatusEnums();
  }, []);
  const getCampaignStatusDescription = useCallback((statusValue: string) => {
    const foundStatus = campaignStatusEnums.find(enumItem => enumItem.value === statusValue);
    return foundStatus ? foundStatus.description : statusValue;
  }, [campaignStatusEnums]);
  const fetchAllDataForExport = useCallback(async () => {
    const payload = buildUserPayload();
    try {
      const requestBody = [{ field: "clientId", operator: "=", value: clientId }, ...(payload ? [payload] : [])];
      const response = await apiCall("post", `/api/campaign-projects/search?page=0&size=${totalElements}`, requestBody);
      return (
        response?.content?.map((item: any) => ({ campaignName: item.campaignName, projectId: item.projectId, assignedCheckList: item.assignedCheckList, auditStatus: getCampaignStatusDescription(item.auditStatus) })) || []
      );
    } catch (error) {
      console.error("Error exporting data:", error);
      return [];
    }
  }, [totalElements, clientId, getCampaignStatusDescription]);
  useEffect(() => {
    fetchData(0, itemsPerPage);
  }, [fetchData, itemsPerPage]);
  const handleDetailsPage = (campaignId: string, projectId: string) => {
    navigate(`/auditorchecklistdetails/${campaignId}/${projectId}`);
  };
  const handleViewExecuteChecklist = (row: any) => {
    navigate(`/auditorviewchecklistdetails/${row.campaignId}/${row.projectId}`);
  };
  const handleExportChecklistClick = (row: any) => {
    handleExportChecklist(row.localTemplateId, row.templateName || row.assignedCheckList, row.campaignId, row.projectId);
  };
  return (
    <div className="table-details">
      <div className="table-title">
        <h2>Audit</h2>
        <div className="table-title-actions">
          <ExportButton data={data} columns={exportColumns} fetchAllData={fetchAllDataForExport} fileName={`Audits`} />
        </div>
      </div>
      <div className="table-container">
        <div className="table-inner-container">
          <table className="main-table">
            <thead>
              <tr>
                {columns.map((column) => (
                  <th key={column.key}>{column.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={columns.length} className="no-data">Loading...</td>
                </tr>
              ) : Array.isArray(data) && data.length === 0 ? (
                <tr>
                  <td colSpan={columns.length} className="no-data">No data available</td>
                </tr>
              ) : (
                data.map((row, index) => (
                  <tr key={`${row.campaignId}-${row.projectId}-${index}`}>
                    <td>{(currentPage - 1) * itemsPerPage + index + 1}</td>
                    <td>{row.campaignName || "N/A"}</td>
                    <td>{row.projectId || "N/A"}</td>
                    <td>{row.assignedCheckList || "N/A"}</td>
                    <td>{getCampaignStatusDescription(row.auditStatus || "N/A")}</td>
                    <td>
                      {fields.view && (
                        <FaEye className="action-button" onClick={() => handleViewExecuteChecklist(row)} title="View Execute" />
                      )}
                      {fields.create && row.auditStatus !== "CLOSED" && row.campaignStatus !== "NOT_STARTED" && !isAuditExpired(row.campaignEndDate) && (<FaPlus className="action-button" onClick={() => handleDetailsPage(row.campaignId, row.projectId)} title="Execute Audit" />)}
                      <FaFileExport className="action-button" title="Export" onClick={() => handleExportChecklistClick(row)} />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      <Pagination currentPage={currentPage} totalPages={totalPages} totalElements={totalElements} itemsPerPage={itemsPerPage} onPageChange={handlePageChange} onItemsPerPageChange={handleItemsPerPageChange} />
      {message && (<ToastPopup message={message.text} type={message.type} duration={message.duration} onClose={() => setMessage(null)} />)}
    </div>
  );
};
export default AuditorCheckListManagement;